import { Collapse, List, ListItem, ListItemButton } from "@mui/material";
import withStyles from '@mui/styles/withStyles';
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import classNames from "classnames";
import Immutable from "immutable";
import React from "react";
import { Link } from "react-router-dom";

import ItemAvatar from "./ItemAvatar";
import ItemIcon from "./ItemIcon";
import ItemText from "./ItemText";
import getMenuIcon from "./get-menu-icon";

function styles(theme) {
  return {
    // Estilos base do item; visual (bg, cor, hover, ativo) é controlado pelo
    // MenuDrawer via `& .MuiListItemButton-root { ... }` pra ficar consistente
    // com o gradient azul do sidebar.
    button: {
      "&:focus": { backgroundColor: "transparent" },
    },
    // Ícone do "expandir submenu" — branco translúcido sobre o sidebar azul.
    expandIcon: {
      color: "rgba(255, 255, 255, 0.7)",
    },
    expandIconOpen: {
      color: "#ffffff",
    },
    depth2: { paddingRight: 0 },
    depth3: {
      paddingLeft: theme.spacing(4),
      paddingRight: 0,
    },
  };
}

/*
 * ---------------------------------------------------------
 *
 * Utilidades
 *
 * ---------------------------------------------------------
 */

function createHandleClick(params) {
  const { closedParallelAndDeeper, toggleSubMenu, onClick, props, depth, href, link, key } = params;
  const { switchDrawerFocus, onToggleDrawer, isLargeScreen, drawerOpen } = props;

  if (!link && !href) {
    return () => {
      toggleSubMenu(depth, key);
      if (onClick) {
        onClick();
      }
    };
  }

  return (e) => {
    closedParallelAndDeeper(depth);
    if (!isLargeScreen) {
      switchDrawerFocus(false);
      onToggleDrawer();
    } else {
      switchDrawerFocus(drawerOpen);
    }
    if (onClick) {
      onClick();
    }
  };
}

function getLinkProps(href, link) {
  // href absoluto (URL externa, ex: trex) ganha sobre link (rota SPA).
  // Necessário porque o quokka manda ambos quando o menu é AMBIENTE=TREX,
  // e o coruja v2 (standalone) não tem o esquilo pra fazer o proxy.
  if (href && /^https?:\/\//.test(href)) {
    return { component: "a", href };
  }
  if (link) {
    return { component: Link, to: link };
  }
  if (href) {
    return { component: "a", href };
  }
  return null;
}

function getClassName(depth, classes) {
  const classOptions = ["", "", classes.depth2, classes.depth3, ""];
  return classNames(classes.button, depth > 0 && classOptions[depth]);
}

function getIsCurrentPage(itemURL, applet, depth) {
  if (!itemURL) {
    return false;
  }

  // No coruja v2 standalone, a URL inclui o prefix /src/apps/<X>/
  // (que o BrowserRouter usa como basename). O itemURL do menu vem
  // como path "interno" (ex: /formandos). Removemos o prefix.
  let current = window.location.pathname;
  const basenameMatch = current.match(/^(\/src\/apps\/[^/]+)/);
  if (basenameMatch) {
    current = current.slice(basenameMatch[1].length) || "/";
  }

  // Normaliza trailing slash pra comparação consistente
  const stripSlash = (s) => (s !== "/" && s.endsWith("/") ? s.slice(0, -1) : s);
  current = stripSlash(current);
  const normalizedItemURL = stripSlash(itemURL);

  if (applet === "/") {
    return normalizedItemURL === applet && current === applet;
  }

  if (depth >= 1) {
    return current === normalizedItemURL;
  }

  // Top-level (depth 0): destaca se a URL atual É igual ao itemURL OU
  // está dentro dele (sub-rota). Robusto contra trailing slash.
  return (
    current === normalizedItemURL ||
    current.startsWith(`${normalizedItemURL}/`)
  );
}

/*
 * ---------------------------------------------------------
 *
 * Funções de renderização
 *
 * ---------------------------------------------------------
 */

function renderMenuList(params) {
  const {
    props,
    depth,
    isOpen,
    parent,
    isFocused,
    itemProps,
    currentDepth,
    toggleSubMenu,
    noDefaultIcon,
    closedParallelAndDeeper,
  } = params;
  const { applet } = props;

  const { classes, open: drawerOpen, isLargeScreen } = props;
  const href = itemProps.get("href");
  const link = itemProps.get("link");
  const label = itemProps.get("label");
  const bold = itemProps.get("bold", false);
  const isUserName = itemProps.get("isUserName", false);
  const avatar = itemProps.get("avatar");
  const paramIcon = itemProps.get("icon");
  const submenu = itemProps.get("submenu");
  const onClick = itemProps.get("onClick");
  const avatarLetter = itemProps.get("avatarLetter");
  const secondaryLabel = itemProps.get("secondaryLabel");
  const itemURL = href || link;
  const key = `${label}-${parent}-${depth}`;
  const isSubmenuOpen = isOpen(depth, key);
  const open = isLargeScreen
    ? isSubmenuOpen && isLargeScreen && (drawerOpen || isFocused)
    : isSubmenuOpen;
  const selected = depth > 0 && depth === currentDepth;
  const hasSubmenu = submenu && submenu.size > 0;
  const isCurrentPage = getIsCurrentPage(itemURL, applet, depth);

  // Cor do ícone vem do CSS do ListItemIcon (controlado pelo MenuDrawer + ItemIcon).
  // `getMenuIcon` não precisa mais do flag `white` — tanto MUI quanto MDI herdam.
  const icon =
    depth < 1 ? getMenuIcon(paramIcon, Boolean(submenu), noDefaultIcon) : null;

  let expandMenuIcon = null;

  // Seta de submenu só aparece quando o drawer está expandido.
  // Quando colapsado, deixamos só o ícone — clicar no item já abre o submenu
  // ao expandir o drawer (efeito hover-expand do MenuDrawer).
  if (hasSubmenu && drawerOpen) {
    expandMenuIcon = open ? (
      <ArrowDropUp className={classes.expandIconOpen} />
    ) : (
      <ArrowDropDown className={classes.expandIcon} />
    );
  }

  return (
    <React.Fragment key={key}>
      {/* MUI v9: <ListItem button> foi removido — agora é <ListItemButton>.
          Sem isso, a prop `button={true}` vaza pro <li> DOM, gerando warning
          do React e perdendo o feedback de hover/click. */}
      <ListItemButton
        key={key}
        selected={selected || isCurrentPage}
        {...getLinkProps(href, link)}
        className={getClassName(depth, classes)}
        sx={{ cursor: "pointer" }}
        onClick={createHandleClick({
          closedParallelAndDeeper,
          toggleSubMenu,
          onClick,
          props,
          depth,
          href,
          link,
          key,
        })}
      >
        {depth < 1 && icon && (
          <ItemIcon
            isCurrentPage={isCurrentPage}
            isSubmenuOpen={isSubmenuOpen}
            drawerOpen={drawerOpen}
            depth={depth}
            icon={icon}
          />
        )}
        {(avatar || avatarLetter) && (
          <ItemAvatar avatarLetter={avatarLetter} drawerOpen={drawerOpen} avatar={avatar} />
        )}
        <ItemText
          secondaryLabel={secondaryLabel}
          isCurrentPage={isCurrentPage}
          isSubmenuOpen={isSubmenuOpen}
          hasSubmenu={hasSubmenu}
          drawerOpen={drawerOpen}
          label={label}
          bold={bold}
          isUserName={isUserName}
          depth={depth}
        />
        {expandMenuIcon}
      </ListItemButton>
      {hasSubmenu && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {submenu
              .map((s) =>
                renderMenuList({
                  closedParallelAndDeeper,
                  depth: depth + 1,
                  toggleSubMenu,
                  noDefaultIcon,
                  currentDepth,
                  itemProps: s,
                  parent: key,
                  isFocused,
                  isOpen,
                  props,
                }),
              )
              .toArray()}
          </List>
        </Collapse>
      )}
    </React.Fragment>
  );
}

/*
 * ---------------------------------------------------------
 *
 * Componente
 *
 * ---------------------------------------------------------
 */

function closedParallelAndDeeper(state, depth) {
  return {
    0: depth < 1 ? null : state[0],
    1: depth < 2 ? null : state[1],
    2: depth < 3 ? null : state[2],
    3: depth < 4 ? null : state[3],
  };
}

function useMenuState() {
  const [state, setState] = React.useState({});
  const keys = Object.keys(state);
  const currentDepth = Immutable.List(keys)
    .map((k) => (state[k] ? parseInt(k, 10) : -1))
    .takeWhile((k) => k >= 0)
    .reduce((acc, k) => (k > acc ? k : acc), 0);

  return {
    currentDepth: currentDepth + 1,
    isOpen: (depth, key) => key === state[depth],
    closedParallelAndDeeper: (depth) => setState(closedParallelAndDeeper(state, depth)),
    toggleSubMenu: (depth, id) => {
      const openned = state[depth];
      setState({ ...state, [depth]: openned === id ? null : id });
    },
  };
}

function DashboardMenu(props) {
  const {
    isFocused,
    noDefaultIcon = false,
    menu = Immutable.List([]),
    style,
    className,
    ...other
  } = props;

  const { closedParallelAndDeeper, toggleSubMenu, currentDepth, isOpen } = useMenuState();

  return (
    <List component="div" style={style} className={className}>
      {menu
        .map((item) =>
          renderMenuList({
            closedParallelAndDeeper,
            itemProps: item,
            toggleSubMenu,
            noDefaultIcon,
            currentDepth,
            props: other,
            parent: "",
            isFocused,
            depth: 0,
            isOpen,
          }),
        )
        .toArray()}
    </List>
  );
}

export default React.memo(withStyles(styles)(DashboardMenu));
